<script>
import { mapState, mapActions } from 'vuex'
import VueApexCharts from 'vue-apexcharts'

export default {
   components: {
      VueApexCharts,
   },
   data() {
      return {
         // b-tabs menggunakan index numerik sebagai nilai v-model.
         activeTab: 0,
         // Flag loading untuk menampilkan overlay spinner saat request berjalan.
         loading: false,
      }
   },
   computed: {
      ...mapState({
         nibPerSkalaUsaha: state => state.DashboardOss.nibPerSkalaUsaha,
         tahun: state => state.DashboardOss.tahun,
         refreshTimestamp: state => state.DashboardOss.refreshTimestamp,
         skin: state => state.appConfig.layout.skin,
      }),
      isDark() {
         return this.skin === 'dark'
      },
      chartData() {
         return this.nibPerSkalaUsaha.data || {}
      },
      chartSeries() {
         return [
            {
               name: 'NIB',
               data: [
                  this.chartData.mikro || 0,
                  this.chartData.kecil || 0,
                  this.chartData.menengah || 0,
                  this.chartData.besar || 0,
               ],
            },
         ]
      },
      chartOptions() {
         const textColor = this.isDark ? '#b4b7bd' : '#6e6b7b'
         const gridColor = this.isDark ? '#3b4253' : '#ebe9f1'

         return {
            chart: {
               type: 'bar',
               foreColor: textColor,
               toolbar: {
                  show: false,
               },
               fontFamily: 'Montserrat, Helvetica, Arial, sans-serif',
            },
            colors: ['#3b9df8', '#3b9df8', '#ea5455', '#ea5455'],
            plotOptions: {
               bar: {
                  horizontal: false,
                  borderRadius: 4,
                  columnWidth: '50%',
               },
            },
            dataLabels: {
               enabled: true,
               formatter(val) {
                  return val > 0 ? val.toLocaleString('id-ID') : ''
               },
            },
            grid: {
               borderColor: gridColor,
               strokeDashArray: 4,
            },
            xaxis: {
               categories: ['Mikro', 'Kecil', 'Menengah', 'Besar'],
               labels: {
                  style: {
                     colors: textColor,
                  },
               },
               title: {
                  text: 'Skala Usaha',
                  style: {
                     color: textColor,
                  },
               },
            },
            yaxis: {
               min: 0,
               forceNiceScale: true,
               title: {
                  text: 'Jumlah NIB',
                  style: {
                     color: textColor,
                  },
               },
               labels: {
                  style: {
                     colors: [textColor],
                  },
                  formatter: value => Math.round(value).toLocaleString('id-ID'),
               },
            },
            tooltip: {
               theme: this.isDark ? 'dark' : 'light',
               y: {
                  formatter: value => `${value.toLocaleString('id-ID')} NIB`,
               },
            },
            legend: {
               labels: {
                  colors: textColor,
               },
            },
         }
      },
      totalUmk() {
         return this.chartData.umk || 0
      },
      totalNonUmk() {
         return this.chartData.nonumk || 0
      },
   },
   watch: {
      tahun(newYear) {
         this.loadData(newYear)
      },
      activeTab() {
         this.loadData(this.tahun)
      },
      refreshTimestamp() {
         this.loadData(this.tahun)
      },
   },
   mounted() {
      this.loadData(this.tahun)
   },
   methods: {
      ...mapActions({
         getNibPerSkalaUsaha: 'DashboardOss/getNibPerSkalaUsaha',
      }),
      async loadData(year) {
         this.loading = true
         try {
            // Ubah index tab BootstrapVue ke nilai yang dipahami oleh action.
            const tab = this.activeTab === 0 || this.activeTab === 'tahun'
               ? 'tahun'
               : 'all_time'
            await this.getNibPerSkalaUsaha({ year, tab })
         } catch (error) {
            // Chart tetap menampilkan data kosong saat request gagal.
         } finally {
            this.loading = false
         }
      },
   },
}
</script>

<template>
   <b-row>
      <b-col cols="12">
         <b-overlay
            :show="loading"
            :variant="isDark ? 'dark' : 'white'"
            spinner-variant="primary"
            blur="0"
            opacity=".75"
            rounded="sm"
         >
            <b-card class="card-wrapper">
               <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-1">
                  <div class="mb-1 mb-md-0">
                     <h4 class="mb-25">
                        NIB per Skala Usaha
                     </h4>
                     <small class="text-muted">Distribusi NIB berdasarkan skala usaha</small>
                  </div>
               </div>

               <div class="mb-1">
                  <b-tabs v-model="activeTab" pills>
                     <b-tab :title="`Tahun ${tahun}`" name="tahun" />
                     <b-tab title="All Time" name="all_time" />
                  </b-tabs>
               </div>

               <b-row class="mb-1">
                  <b-col cols="6">
                     <div class="d-flex align-items-center p-1 rounded status-card status-card-success">
                        <div class="stat-icon icon-success mr-1">
                           <feather-icon icon="TrendingUpIcon" size="16" />
                        </div>
                        <div>
                           <small class="text-muted">Total UMK</small>
                           <div class="font-weight-bolder status-value">
                              {{ totalUmk.toLocaleString('id-ID') }}
                           </div>
                           <small class="text-muted">Mikro & Kecil</small>
                        </div>
                     </div>
                  </b-col>
                  <b-col cols="6">
                     <div class="d-flex align-items-center p-1 rounded status-card status-card-danger">
                        <div class="stat-icon icon-danger mr-1">
                           <feather-icon icon="BriefcaseIcon" size="16" />
                        </div>
                        <div>
                           <small class="text-muted">Total Non UMK</small>
                           <div class="font-weight-bolder status-value">
                              {{ totalNonUmk.toLocaleString('id-ID') }}
                           </div>
                           <small class="text-muted">Menengah & Besar</small>
                        </div>
                     </div>
                  </b-col>
               </b-row>

               <vue-apex-charts
                  type="bar"
                  height="350"
                  :options="chartOptions"
                  :series="chartSeries"
               />
            </b-card>
         </b-overlay>
      </b-col>
   </b-row>
</template>

<style scoped>
.card-wrapper {
   border: 0;
   border-radius: 12px;
   box-shadow: 0 4px 24px rgba(34, 41, 47, 0.08);
}

.stat-icon {
   width: 32px;
   height: 32px;
   display: flex;
   align-items: center;
   justify-content: center;
   border-radius: 8px;
}

.icon-success { color: #28c76f; background: #fff; }
.icon-danger { color: #ea5455; background: #fff; }

.status-card {
   transition: background-color 0.2s ease;
}

.status-card-success { background-color: #e5f8ed; }
.status-card-danger { background-color: #fce8e8; }

.status-card-success .status-value { color: #28c76f; }
.status-card-danger .status-value { color: #ea5455; }

.dark-layout .status-card-success { background-color: rgba(40, 199, 111, 0.2); }
.dark-layout .status-card-danger { background-color: rgba(234, 84, 85, 0.2); }

.dark-layout .icon-success { background: rgba(40, 199, 111, 0.2); }
.dark-layout .icon-danger { background: rgba(234, 84, 85, 0.2); }
</style>
